/**
 * How the integration vocabulary is worded on screen, and what can be read off
 * an integration row.
 *
 * The enums and the verdict itself live in `@wend/shared` and are never
 * re-declared here. In particular **`sesReadiness()` is imported, not
 * re-implemented**: the API calls it to decide whether a campaign may be
 * scheduled and this screen calls it to decide which badge to paint, and two
 * implementations of "is this account green" would drift — the way they drift is
 * that one of them quietly stops counting the sandbox. This file decides only
 * what each state is *called*, which semantic tone it carries, and how AWS's own
 * numbers are read aloud.
 *
 * Tone is semantic and never decorative. Only one way of being connected is
 * green, and the label always says the state too, so colour is never the only
 * carrier.
 *
 * ## The one thing this file will not do
 *
 * It invents no number and no claim about a tenant's AWS account. Every figure
 * on the card — the daily cap, the send rate, what has been sent, which
 * identities exist — comes out of the check result the API returned. The
 * functions below format those values; they never supply a default for one that
 * is missing, because a plausible-looking quota nobody measured is exactly the
 * kind of thing a person would plan a campaign around.
 */
import {
  sesReadiness,
  type SesIntegration,
  type IntegrationStatus,
  type SesCheckResult,
  type SesFailureCode,
  type SesQuota,
  type SesReadiness,
  type SesReadinessState,
  type SesTransport,
} from '@wend/shared';
import type { BadgeTone, CalloutTone } from '@wend/ui';

import { formatCount, pluralise } from './format';

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

export interface IntegrationBadge {
  readonly label: string;
  readonly tone: BadgeTone;
}

/**
 * The badge for each member of `IntegrationStatus`.
 *
 * Two of the four cannot happen in slice 3 and are worded for the day they can
 * rather than left as placeholders:
 *
 *   - `disconnected` is the column default and is never written. Disconnecting
 *     **deletes the row**, so "not connected" is the absence of a row — a status
 *     saying `disconnected` on a row still holding a live ciphertext would be the
 *     more misleading of the two;
 *   - `expiring` means nothing for an SES access key, which carries no expiry we
 *     can see coming. It is in the enum for Meta's WhatsApp tokens in slice 7.
 *
 * `connected` is the coarse answer — the credentials authenticate — and it is
 * refined by {@link readSesIntegration} into one of the three ways to be
 * connected. It is deliberately not green here.
 */
export const INTEGRATION_STATUS_BADGE: Readonly<Record<IntegrationStatus, IntegrationBadge>> = {
  disconnected: { label: 'Not connected', tone: 'neutral' },
  connected: { label: 'Connected', tone: 'neutral' },
  failed: { label: 'Not working', tone: 'bad' },
  expiring: { label: 'Expiring', tone: 'warn' },
};

/* -------------------------------------------------------------------------- */
/* Readiness — the three ways to be connected, worded                          */
/* -------------------------------------------------------------------------- */

export interface ReadinessDisplay {
  /** The badge on the card header. */
  readonly badge: IntegrationBadge;
  /** The tone of the callout, when the state warrants one. `null` for green. */
  readonly calloutTone: CalloutTone | null;
  /** The callout's first line. Present wherever `calloutTone` is. */
  readonly title: string | null;
}

/**
 * One entry per member of `SesReadinessState`, and the wording is the point.
 *
 * "Sandboxed" and "no verified identity" are not broken connections — the
 * credentials work — so neither is red, and neither is allowed to read as a
 * fault the tenant caused. They are also not small print: a person who
 * discovers the sandbox when a campaign reaches nobody has been failed by this
 * screen, so both get a `warn` Callout rather than a line of muted text.
 */
export const SES_READINESS_DISPLAY: Readonly<Record<SesReadinessState, ReadinessDisplay>> = {
  ready: {
    badge: { label: 'Ready to send', tone: 'good' },
    calloutTone: null,
    title: null,
  },
  sandboxed: {
    badge: { label: 'Sandboxed', tone: 'warn' },
    calloutTone: 'warn',
    title: 'This AWS account is still in the Amazon SES sandbox',
  },
  no_verified_identity: {
    badge: { label: 'Nothing to send from', tone: 'warn' },
    calloutTone: 'warn',
    title: 'This AWS account has no verified sending identity',
  },
  /*
   * Deliberately the quietest entry in this table, and the only one that is not
   * `warn`.
   *
   * The connection works. AWS authenticated the key and then declined the two
   * read calls, which is an IAM policy scoped to sending — a reasonable thing
   * for a tenant to hand us, not a fault and not something they need to repair
   * before they can work. `info`, so it reads as a note rather than a warning,
   * and the label says what Wend cannot do rather than what the tenant did
   * wrong.
   *
   * It must never be styled up to `warn` to "make sure they notice". The one
   * thing a tenant might genuinely miss is a sandbox Wend cannot see, and that
   * is said inside the callout body rather than by colouring the badge.
   */
  account_not_readable: {
    badge: { label: 'Connected', tone: 'neutral' },
    calloutTone: 'info',
    title: 'Wend cannot read this AWS account',
  },
};

/**
 * The account-level pause, which is a flag rather than a readiness state.
 *
 * `sesReadiness()` deliberately leaves `sendingEnabled` out of `state` — a
 * paused account is a fourth situation and the contract does not invent a
 * verdict for it. The card still has to render it, and it renders it as red,
 * because AWS having switched sending off is a harder stop than the sandbox: not
 * one message leaves, to a verified address or otherwise.
 */
export const SES_SENDING_PAUSED: {
  readonly badge: IntegrationBadge;
  readonly calloutTone: CalloutTone;
  readonly title: string;
} = {
  badge: { label: 'Sending paused by AWS', tone: 'bad' },
  calloutTone: 'bad',
  title: 'Amazon SES has paused sending on this AWS account',
};

/**
 * Connected, and never successfully checked — which is not the same as anything
 * being wrong with the AWS account.
 *
 * Every SES flag Wend holds is a copy of a check result: `sendingEnabled`,
 * `sandboxed` and `sendableIdentityCount` all sit at their "not connected"
 * defaults until a check succeeds, and `connectStamp` stores the row with
 * `last_check = null` whenever the connect-time check fails — a rejected key, an
 * IAM policy without `ses:GetAccount`, a timeout. Read without consulting
 * `checkedAt` first, those defaults say Amazon has paused the account and there
 * is no verified sender, neither of which anybody has observed.
 *
 * So this exists as its own state, and it is `warn` rather than `bad`: the
 * repair is one button on Integrations, not a support case with AWS. Shared by
 * the Integrations card and the Overview so the two cannot describe the same
 * row differently.
 */
/**
 * A saved credential whose last connection test did not succeed — the row's
 * `status` is `failed`, which the Overview carries as `lastCheckFailed`.
 *
 * Its own state rather than `SES_NOT_TESTED` with different words, because the
 * two used to be one: a connect whose test failed stores no check, so the
 * Overview said of a wrong secret that it had "not been looked at yet", and a
 * re-test that failed kept the last good check, so a rotated key read as
 * healthy (review). `warn`, not `bad`, for the reason `SES_NOT_TESTED` is: the
 * repair is on Integrations, where the error itself is shown.
 */
export const CHECK_FAILED_DISPLAY: {
  readonly badge: IntegrationBadge;
  readonly title: { readonly email: string; readonly whatsapp: string };
  readonly note: string;
} = {
  badge: { label: 'Last test failed', tone: 'warn' },
  title: {
    email: 'The last connection test of this AWS account failed',
    whatsapp: 'The last connection test of this WhatsApp number failed',
  },
  note: 'Wend’s last check of these credentials did not succeed. The reason is on Integrations.',
};

export const SES_NOT_TESTED: {
  readonly badge: IntegrationBadge;
  readonly calloutTone: CalloutTone;
  readonly title: string;
} = {
  badge: { label: 'Not tested yet', tone: 'warn' },
  calloutTone: 'warn',
  title: 'This AWS account has not been checked yet',
};

/* -------------------------------------------------------------------------- */
/* Failures                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What each failure code means, in one line, in plain words.
 *
 * These are **headings, not repairs.** The repair is the sentence the API sent —
 * that mapping is a table in the SES port and it is the only one, so restating it
 * here would give a person two accounts of the same failure that could disagree.
 * What this adds is the half a stored message cannot carry on its own: which of
 * the six things went wrong, at a glance, above the paragraph that says what to
 * do about it.
 *
 * The three that matter most are three different jobs. A wrong access key ID is a
 * re-paste, a wrong secret usually means creating a new key because a secret
 * cannot be read back from the AWS console, and a permission failure is an IAM
 * policy edit that no new key will fix.
 */
export const SES_FAILURE_HEADING: Readonly<Record<SesFailureCode, string>> = {
  invalid_access_key: 'AWS does not recognise this access key ID',
  invalid_secret: 'The secret access key does not match this key ID',
  access_denied: 'These credentials are not allowed to read the SES account',
  invalid_region: 'Amazon SES could not be reached in that region',
  network: 'Wend could not reach Amazon SES',
  unknown: 'Amazon SES answered with an error Wend does not recognise',
};

/**
 * The heading for a failure whose code we no longer have.
 *
 * `integrations.last_error` stores the message but not the code, so a check that
 * failed before this page was opened arrives as a sentence with nothing to key
 * off. The heading is therefore general and the message under it is not — which
 * is the right way round, because the message is the specific half.
 */
export const SES_FAILURE_HEADING_STORED = 'The last check of these credentials failed';

/* -------------------------------------------------------------------------- */
/* The stub                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What the screen says when the answer came from `SES_TRANSPORT=stub`.
 *
 * Small and factual, not alarming: nothing is wrong, the answer is simply not
 * from AWS. It is said at all because a mock mistaken for the real thing has
 * already cost this project a verification, and a result that cannot say where it
 * came from is how that happens again.
 */
export const SES_STUB_NOTICE =
  'Simulated — no AWS call was made. This Wend API is running with SES_TRANSPORT=stub, so the figures below were fabricated locally rather than returned by Amazon.';

/**
 * The badge that goes with it, and the reason it exists.
 *
 * Slice 16 promoted this badge onto the tab rail, where it is read from the
 * *other* tab — and until this constant existed, a stub-answered check with
 * healthy fields produced a green "Ready to send" on the strip and in the card
 * header, directly above {@link SES_STUB_NOTICE} saying every figure under it
 * was fabricated. On the card that was a badge contradicting the sentence
 * beside it; on the rail it is a green tick with the retraction on a panel that
 * is `display: none`. "Ready to send" about an account nobody called is not a
 * softer version of the truth, it is a different claim.
 *
 * `warn` rather than `bad`: nothing is broken, the answer simply is not
 * Amazon's — the same register the notice itself is written in.
 */
/* Not exported: nothing outside this file has a use for it, and the screens
 * that show it get it through `readSesIntegration`'s badge like every other
 * state. `verify-integrations-tabs.tsx` asserts the word as a literal, which
 * is the right instrument — a test importing this constant would follow a
 * rewording into agreeing with itself. */
const SES_SIMULATED_BADGE: IntegrationBadge = { label: 'Simulated', tone: 'warn' };

/* -------------------------------------------------------------------------- */
/* Reading AWS's own numbers aloud                                             */
/* -------------------------------------------------------------------------- */

/**
 * The 24-hour send quota.
 *
 * AWS reports `-1` for an account with no daily cap, so the best possible answer
 * arrives as a negative number and must not be printed as one.
 */
export function formatDailyQuota(max24HourSend: number): string {
  if (max24HourSend < 0) return 'No daily cap';
  return `${pluralise(max24HourSend, 'message')} a day`;
}

/**
 * The send rate, in messages per second.
 *
 * A float, not an integer — AWS reports `1`, `14`, and fractions below one — so
 * this formats through the same number formatter as everything else rather than
 * rounding a rate of `0.5` down to nothing.
 */
export function formatSendRate(maxSendRate: number): string {
  if (maxSendRate < 0) return 'No rate cap';
  return `${pluralise(maxSendRate, 'message')} a second`;
}

/** What the account has sent in the last 24 hours, against its cap. */
export function formatSentLast24Hours(quota: SesQuota): string {
  const sent = formatCount(quota.sentLast24Hours);
  return quota.max24HourSend < 0 ? sent : `${sent} of ${formatCount(quota.max24HourSend)}`;
}

/**
 * The sandbox's limits, read out of the account's own quota rather than out of
 * the documentation.
 *
 * AWS's sandbox is "about 200 a day and one a second" in the general case, and
 * quoting that from memory would put a number on the screen that nobody
 * measured. These are the numbers `GetAccount` returned for this account.
 *
 * **Null takes that rule to its conclusion rather than around it.** A key
 * without `ses:GetAccount` has no quota, and the temptation is to fall back to
 * the documented 200-a-day — which is the exact unmeasured number the paragraph
 * above refuses. So the phrase keeps the shape of the sentence it is embedded
 * in and makes no numeric claim at all. In practice this is unreachable, since
 * a `sandboxed` verdict requires `productionAccess === false` and therefore
 * requires `GetAccount` to have answered; it is written because a caller that
 * stops being true is how the unmeasured number would get in.
 */
export function describeSandboxLimits(quota: SesQuota | null): string {
  if (quota === null) return 'a daily cap and a rate cap Wend cannot read';
  return `${formatDailyQuota(quota.max24HourSend).toLowerCase()}, at most ${formatSendRate(
    quota.maxSendRate,
  ).toLowerCase()}`;
}

/**
 * An SESv2 identity type, for display.
 *
 * Unrecognised values pass through unchanged. `identityType` is AWS's
 * vocabulary, not ours — the contract keeps it as a plain string for that exact
 * reason — so a type AWS adds later renders as itself rather than as "Unknown".
 */
export function identityTypeLabel(identityType: string): string {
  switch (identityType) {
    case 'EMAIL_ADDRESS':
      return 'Email address';
    case 'DOMAIN':
      return 'Domain';
    case 'MANAGED_DOMAIN':
      return 'Managed domain';
    default:
      return identityType;
  }
}

/* -------------------------------------------------------------------------- */
/* The reading                                                                 */
/* -------------------------------------------------------------------------- */

export interface SesReading {
  /** A row exists: this workspace has connected an SES account. */
  readonly connected: boolean;
  /** The last check did not authenticate, or never reached AWS. */
  readonly failing: boolean;
  /**
   * The snapshot from the last **successful** check, or null if none has ever
   * succeeded. Every figure the card shows comes from here.
   */
  readonly check: SesCheckResult | null;
  /** The verdict on that snapshot, from `@wend/shared`. Null when there is none. */
  readonly readiness: SesReadiness | null;
  /**
   * There is a snapshot, and it is history rather than the current state.
   *
   * A re-check that fails leaves the previous success in place on purpose —
   * "failing now, last verified on Tuesday" is true and useful. But the figures
   * in that snapshot describe a moment that has passed, so the card has to label
   * them as such instead of showing a quota beside a broken connection as though
   * both were current.
   */
  readonly stale: boolean;
  /** The header badge: one label, one tone, and the label says the state. */
  readonly badge: IntegrationBadge;
}

/**
 * Everything the SES card needs to decide what to render, derived once.
 *
 * The badge rule, in order of who wins:
 *
 *   1. **failing** beats everything. A stale green snapshot must never paint a
 *      green badge over credentials that stopped working;
 *   2. **a stub answered** beats every remaining verdict, because every one of
 *      them is computed from figures the stub made up. This shipped wrong and
 *      slice 16 made it worse rather than introducing it: the badge was always
 *      derived without reading `check.transport`, which put a green tick beside
 *      `SES_STUB_NOTICE` on the card, and the tab rail then started drawing that
 *      same badge on a strip where the retracting sentence is on a hidden panel.
 *      It ranks below `failing` because a failed check is a real observation and
 *      names a real repair, and above everything else because the rest are
 *      claims about an AWS account nobody called;
 *   3. **AWS has paused sending** beats the readiness state. `sesReadiness()`
 *      still calls a paused account `ready`, correctly — it is not inventing a
 *      fourth verdict — but a green tick over an account that cannot send one
 *      message is a claim this screen is not willing to make. The state is
 *      unchanged; only the badge refuses to go green;
 *   4. otherwise the readiness state decides, and only `ready` is green;
 *   5. connected with no successful check at all is `warn`, not green. It should
 *      not happen — a connect runs a check — but a card that assumed it could not
 *      would render a green badge from an empty snapshot if it ever did.
 */
export function readSesIntegration(integration: SesIntegration | null): SesReading {
  if (integration === null) {
    return {
      connected: false,
      failing: false,
      check: null,
      readiness: null,
      stale: false,
      badge: INTEGRATION_STATUS_BADGE.disconnected,
    };
  }

  const check = integration.lastCheck;
  const readiness = check === null ? null : sesReadiness(check);
  const failing = integration.status === 'failed';

  return {
    connected: true,
    failing,
    check,
    readiness,
    stale: failing && check !== null,
    /*
     * The transport is read off the snapshot the badge is about, so a row whose
     * figures came from a stub cannot be badged from those figures. There is no
     * fallback to the row: `transport` exists only on a check result, and a row
     * with no check is already `SES_NOT_TESTED` two lines down.
     */
    badge: badgeFor(integration.status, readiness, check?.transport ?? null),
  };
}

function badgeFor(
  status: IntegrationStatus,
  readiness: SesReadiness | null,
  transport: SesTransport | null,
): IntegrationBadge {
  if (status !== 'connected') return INTEGRATION_STATUS_BADGE[status];
  if (transport === 'stub') return SES_SIMULATED_BADGE;
  if (readiness === null) return SES_NOT_TESTED.badge;
  /*
   * `=== false`, not `!`. Null is a key that cannot call `GetAccount`, and
   * `!null` is true — so the plain negation would badge every narrowly-scoped
   * key as "Paused by AWS", the most alarming badge this function can return,
   * on an account AWS never said anything about. Slice 16 shipped a badge that
   * lied by adjacency; this is the same failure one field further in.
   *
   * An account whose switch we could not read falls through to the readiness
   * state, which is `sandboxed` for an invisible account — conservative, and
   * the callout beside it says which of the two it is.
   */
  if (readiness.sendingEnabled === false) return SES_SENDING_PAUSED.badge;
  return SES_READINESS_DISPLAY[readiness.state].badge;
}
